package com.evoapps.evoshift.domain.market

import kotlinx.serialization.Serializable

/**
 * The country whose labour law and taxes apply to a workplace (where the work is done). Each
 * market has its own [com.evoapps.evoshift.domain.market.payroll.PayrollRules]; the currency and
 * time zone are the defaults for new workplaces there, and [defaultLabourPercent] /
 * [defaultCommissionLabourPercent] the labour share of income the staffing hints aim for until the
 * workplace sets its own (SH-54, SH-95: see [defaultLabourPercentFor]).
 */
@Serializable
enum class Market(
    val id: String,
    val countryCode: String,
    val flag: String,
    val currency: String,
    val timezone: String,
    /**
     * Planned employer labour cost as a share of income, in percent, for a workplace that keeps its
     * whole net sales (no commission, or 100%): the usual hospitality target, 30% of sales.
     */
    val defaultLabourPercent: Double,
    /**
     * SH-95: the same for a commission operator or franchisee, who keeps only a commission on the
     * sales and usually pays neither the goods nor the rent from it: staff are most of their costs.
     * 60% of income leaves 40% for the other costs and the margin; at a 25.2% commission it is
     * about 15% of the sales.
     */
    val defaultCommissionLabourPercent: Double
) {
    POLAND("POLAND", "PL", "🇵🇱", "PLN", "Europe/Warsaw", defaultLabourPercent = 30.0, defaultCommissionLabourPercent = 60.0);

    /**
     * The default labour target, % of income, of a workplace with [commissionPercent] (a fraction):
     * [defaultCommissionLabourPercent] for a commission below 100%, else [defaultLabourPercent].
     */
    fun defaultLabourPercentFor(commissionPercent: Double): Double =
        if (commissionPercent > 0 && commissionPercent < 1) defaultCommissionLabourPercent else defaultLabourPercent

    companion object {
        /** Tolerant lookup by id or ISO country code; unknown or missing means [POLAND]. */
        fun fromId(id: String?): Market {
            val key = id?.trim().orEmpty()
            return entries.firstOrNull { it.id.equals(key, true) || it.countryCode.equals(key, true) } ?: POLAND
        }
    }
}
